import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { apiFetchUrl, resolveWebSocketUrl, getValidAccessToken, refreshAccessToken } from '../lib/api';
import { useAuth } from './AuthContext';
import {
  connectSupportSession,
  disconnectSupportSession,
  getSupportSession,
  SupportSession,
} from '../services/supportApi';

type Phase = 'idle' | 'waiting' | 'connected' | 'ended';

interface EngineerRemoteSupportContextValue {
  session: SupportSession | null;
  phase: Phase;
  loading: boolean;
  error: string | null;
  remoteVideoRef: React.RefObject<HTMLVideoElement | null>;
  remoteStream: MediaStream | null;
  loadSession: (sessionId: string) => Promise<void>;
  attachSession: (session: SupportSession) => void;
  disconnect: (reason?: string) => Promise<void>;
  sendControlEvent: (event: Record<string, unknown>) => void;
  clearError: () => void;
  engineerMatchesUser: (engineerId: string | undefined, user: { _id?: string; email?: string } | null) => boolean;
}

const EngineerRemoteSupportContext = createContext<EngineerRemoteSupportContextValue | null>(null);

function getSupportWsUrl(token: string) {
  return resolveWebSocketUrl(apiFetchUrl(`/ws/support?token=${encodeURIComponent(token)}`));
}

const HEARTBEAT_MS = 25000;

export function EngineerRemoteSupportProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [session, setSession] = useState<SupportSession | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);
  const sessionRef = useRef<SupportSession | null>(null);

  sessionRef.current = session;

  const cleanupWebRtc = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    setRemoteStream(null);
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  }, []);

  const cleanupWs = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendWs = useCallback((type: string, payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  }, []);

  const engineerMatchesUser = useCallback(
    (engineerId: string | undefined, u: { _id?: string; email?: string } | null) => {
      if (!engineerId || !u) return false;
      const eng = String(engineerId).trim().toLowerCase();
      const id = u._id ? String(u._id).trim().toLowerCase() : '';
      const email = u.email ? u.email.trim().toLowerCase() : '';
      return eng === id || (Boolean(email) && eng === email);
    },
    []
  );

  const createPeerConnection = useCallback(
    (s: SupportSession) => {
      cleanupWebRtc();

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;

      pc.onicecandidate = (ev) => {
        if (ev.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          sendWs('webrtc-signal', {
            sessionId: s.sessionId,
            engineerId: s.engineerId,
            customerUserId: s.customerUserId,
            fromUserId: String(user?._id || user?.email || 'engineer'),
            signal: { candidate: ev.candidate },
          });
        }
      };

      pc.ontrack = (ev) => {
        if (ev.streams[0]) {
          setRemoteStream(ev.streams[0]);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = ev.streams[0];
            void remoteVideoRef.current.play().catch(() => undefined);
          }
          setPhase('connected');
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setPhase((p) => (p === 'connected' ? 'waiting' : p));
        }
      };

      return pc;
    },
    [cleanupWebRtc, sendWs, user]
  );

  const ensurePeerConnection = useCallback(
    async (s: SupportSession) => {
      if (!pcRef.current) {
        createPeerConnection(s);
      }
    },
    [createPeerConnection]
  );

  const joinSession = useCallback(
    async (s: SupportSession) => {
      try {
        await connectSupportSession(s.sessionId);
        setPhase('waiting');
        sendWs('join-session', { sessionId: s.sessionId });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to join support session');
      }
    },
    [sendWs]
  );

  const attachSession = useCallback(
    (s: SupportSession) => {
      const normalized = {
        ...s,
        sessionId: String(s.sessionId),
        engineerId: String(s.engineerId),
        customerUserId: String(s.customerUserId),
      };
      if (!normalized.sessionId || normalized.sessionId === 'undefined') return;
      setSession(normalized);
      sessionRef.current = normalized;
      if (normalized.status === 'pending') {
        setPhase('waiting');
        void ensurePeerConnection(normalized);
      } else if (normalized.status === 'connected' || normalized.status === 'approved') {
        void ensurePeerConnection(normalized).then(() => joinSession(normalized));
      }
    },
    [joinSession, ensurePeerConnection]
  );

  const loadSession = useCallback(
    async (sessionId: string) => {
      setLoading(true);
      setError(null);
      try {
        const s = await getSupportSession(sessionId);
        attachSession(s);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
      } finally {
        setLoading(false);
      }
    },
    [attachSession]
  );

  const connectWs = useCallback(
    (token: string) => {
      cleanupWs();
      const ws = new WebSocket(getSupportWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        heartbeatRef.current = setInterval(() => sendWs('heartbeat', {}), HEARTBEAT_MS);
      };

      ws.onmessage = async (ev) => {
        try {
          const msg = JSON.parse(ev.data) as { type: string; payload?: Record<string, unknown> };
          const incoming = msg.payload?.session as SupportSession | undefined;
          const current = sessionRef.current;
          const s =
            incoming &&
            (!current || incoming.sessionId === current.sessionId || current.status === 'pending')
              ? incoming
              : incoming ?? current;

          switch (msg.type) {
            case 'support-request':
            case 'remote-support-request':
              if (s && engineerMatchesUser(s.engineerId, user)) {
                setSession(s);
                sessionRef.current = s;
                setPhase('waiting');
              }
              break;
            case 'support-approved':
            case 'remote-support-approved':
              if (s && engineerMatchesUser(s.engineerId, user)) {
                const normalized = {
                  ...s,
                  sessionId: String(s.sessionId),
                  engineerId: String(s.engineerId),
                  customerUserId: String(s.customerUserId),
                };
                setSession(normalized);
                sessionRef.current = normalized;
                await ensurePeerConnection(normalized);
                await joinSession(normalized);
              }
              break;
            case 'support-connected':
            case 'remote-session-started':
              if (s) {
                const normalized = {
                  ...s,
                  sessionId: String(s.sessionId),
                  engineerId: String(s.engineerId),
                  customerUserId: String(s.customerUserId),
                };
                setSession(normalized);
                sessionRef.current = normalized;
                setPhase('connected');
              }
              break;
            case 'support-ended':
            case 'remote-session-ended':
              setPhase('ended');
              cleanupWebRtc();
              setSession(null);
              sessionRef.current = null;
              break;
            case 'webrtc-signal': {
              const signal = msg.payload?.signal as { type?: string; sdp?: string; candidate?: RTCIceCandidateInit } | undefined;
              if (!signal) break;

              let pc = pcRef.current;
              if (!pc && sessionRef.current) {
                pc = createPeerConnection(sessionRef.current);
              }
              if (!pc) break;

              if (signal.type === 'offer' && signal.sdp) {
                await pc.setRemoteDescription(new RTCSessionDescription(signal as RTCSessionDescriptionInit));
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                sendWs('webrtc-signal', {
                  sessionId: sessionRef.current?.sessionId,
                  engineerId: sessionRef.current?.engineerId,
                  customerUserId: sessionRef.current?.customerUserId,
                  fromUserId: String(user?._id || user?.email || 'engineer'),
                  signal: answer,
                });
              } else if (signal.type === 'answer' && signal.sdp) {
                await pc.setRemoteDescription(new RTCSessionDescription(signal as RTCSessionDescriptionInit));
              } else if (signal.candidate) {
                await pc.addIceCandidate(new RTCIceCandidate(signal.candidate as RTCIceCandidateInit));
              }
              break;
            }
            default:
              break;
          }
        } catch {
          /* ignore malformed payloads */
        }
      };

      ws.onclose = async (ev) => {
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        wsRef.current = null;
        if (aliveRef.current && isAuthenticated) {
          if (ev?.code === 1008) {
            const newToken = await refreshAccessToken();
            if (!newToken) return;
          }
          reconnectRef.current = setTimeout(async () => {
            const token = await getValidAccessToken();
            if (token) connectWs(token);
          }, 3000);
        }
      };
    },
    [cleanupWebRtc, cleanupWs, createPeerConnection, ensurePeerConnection, engineerMatchesUser, joinSession, sendWs, user, isAuthenticated]
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      aliveRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      cleanupWs();
      cleanupWebRtc();
      return;
    }
    aliveRef.current = true;
    const startConnect = async () => {
      const token = await getValidAccessToken();
      if (token && aliveRef.current) connectWs(token);
    };
    void startConnect();
    return () => {
      aliveRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      cleanupWs();
      cleanupWebRtc();
    };
  }, [isAuthenticated, user, connectWs, cleanupWs, cleanupWebRtc]);

  const disconnect = useCallback(
    async (reason?: string) => {
      if (!session) return;
      setLoading(true);
      try {
        await disconnectSupportSession(session.sessionId, reason);
        cleanupWebRtc();
        setSession(null);
        sessionRef.current = null;
        setPhase('ended');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Disconnect failed');
      } finally {
        setLoading(false);
      }
    },
    [cleanupWebRtc, session]
  );

  const sendControlEvent = useCallback(
    (event: Record<string, unknown>) => {
      if (!session) return;
      sendWs('control-event', {
        sessionId: session.sessionId,
        engineerId: session.engineerId,
        customerUserId: session.customerUserId,
        event,
      });
    },
    [sendWs, session]
  );

  return (
    <EngineerRemoteSupportContext.Provider
      value={{
        session,
        phase,
        loading,
        error,
        remoteVideoRef,
        loadSession,
        attachSession,
        disconnect,
        sendControlEvent,
        clearError: () => setError(null),
        remoteStream,
        engineerMatchesUser,
      }}
    >
      {children}
    </EngineerRemoteSupportContext.Provider>
  );
}

export function useEngineerRemoteSupport() {
  const ctx = useContext(EngineerRemoteSupportContext);
  if (!ctx) {
    throw new Error('useEngineerRemoteSupport must be used within EngineerRemoteSupportProvider');
  }
  return ctx;
}
